import React, { useState, useEffect, useMemo } from 'react';
import { Zap, Heart, Star, Repeat, Target, Trophy, CheckCircle, XCircle, Loader } from 'lucide-react';
import './WhereIsIt.css';

// --- Configuración ---
const INITIAL_LIVES = 5;
const CATEGORIES = {
    'in_on_at': { name: 'In, On, At', icon: '📍' },
    'lugar': { name: 'De Lugar', icon: '🗺️' },
    'tiempo': { name: 'De Tiempo', icon: '⏳' },
    'movimiento': { name: 'De Movimiento', icon: '➡️' },
    'ubicacion': { name: 'De Ubicación', icon: '🏠' },
    'complejas': { name: 'Complejas', icon: '🧠' },
    'preposiciones_verbos': { name: 'Con Verbos', icon: '🗣️' },
    'relacion_espacial': { name: 'Relación Espacial', icon: '📐' },
    'preposiciones_adjetivos': { name: 'Con Adjetivos', icon: '😊' }
};

function WhereIsIt() {
    // --- Estados del Juego ---
    const [gameState, setGameState] = useState('menu');
    const [selectedCategory, setSelectedCategory] = useState('in_on_at');
    
    const [questions, setQuestions] = useState([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [feedback, setFeedback] = useState(null);
    const [userAnswer, setUserAnswer] = useState(null);
    const [showCorrectionCard, setShowCorrectionCard] = useState(false);

    const [score, setScore] = useState(0);
    const [lives, setLives] = useState(INITIAL_LIVES);
    const [streak, setStreak] = useState(0);
    const [correctAnswers, setCorrectAnswers] = useState(0);
    const [highScore, setHighScore] = useState(() => parseInt(localStorage.getItem('whereIsItHighScore') || '0', 10));

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const currentQuestion = useMemo(() => questions[currentQuestionIndex], [questions, currentQuestionIndex]);

    const startGame = async () => {
        setIsLoading(true);
        setError('');
        const token = localStorage.getItem('token');
        try {
            // 🔥 CAMBIO CRÍTICO: URL RELATIVA en lugar de absoluta
            const response = await fetch('/api/minigames/get-where-is-it-round', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json', 
                    'Authorization': `Bearer ${token}` 
                },
                body: JSON.stringify({
                    categories: [selectedCategory],
                    seen_sentences: [],
                }),
            });
            if (!response.ok) throw new Error('No se encontraron situaciones para esta categoría.');
            
            const data = await response.json();
            setQuestions(data);
            setCurrentQuestionIndex(0);
            setScore(0);
            setLives(INITIAL_LIVES);
            setStreak(0);
            setCorrectAnswers(0);
            setFeedback(null);
            setUserAnswer(null);
            setShowCorrectionCard(false);
            setGameState('playing');
        } catch (err) {
            setError(err.message);
            setGameState('menu');
        } finally {
            setIsLoading(false);
        }
    };
    
    const endGame = React.useCallback((finalScore = score, finalCorrect = correctAnswers) => {
        if (finalScore > highScore) {
            setHighScore(finalScore);
            localStorage.setItem('whereIsItHighScore', finalScore.toString());
        }

        const saveScore = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) return;
                
                if (finalCorrect > 0) {
                    await fetch('/api/minigames/save-score', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({
                            game_name: 'where_is_it',
                            score: finalScore,
                            correct_answers: finalCorrect,
                            total_questions: questions.length
                        })
                    });
                }
            } catch (error) {
                console.error("Error guardando XP:", error);
            }
        };
        saveScore();

        setGameState('gameOver');
    }, [highScore, questions.length, score, correctAnswers]);

    const moveToNextWord = React.useCallback(() => {
        setShowCorrectionCard(false);
        if (currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex(i => i + 1);
            setFeedback(null);
            setUserAnswer(null);
        } else {
            endGame(score, correctAnswers);
        }
    }, [currentQuestionIndex, questions.length, endGame, score, correctAnswers]);
    
    const handleAnswer = React.useCallback((answer) => {
        if (feedback || showCorrectionCard) return;

        setUserAnswer(answer); 
        const isCorrect = answer.toLowerCase() === currentQuestion.correct_option.toLowerCase();
        
        if (isCorrect) {
            const points = 10 + streak * 2;
            const newScore = score + points;
            const newCorrect = correctAnswers + 1;
            setScore(newScore);
            setCorrectAnswers(newCorrect);
            setStreak(s => s + 1);
            setFeedback({ type: 'correct' });
            setTimeout(moveToNextWord, 1500);
        } else {
            const newLives = lives - 1;
            setLives(newLives);
            setStreak(0);
            if (newLives <= 0) {
                // Muestra la tarjeta de corrección brevemente antes de terminar el juego
                setShowCorrectionCard(true);
                setTimeout(() => endGame(score, correctAnswers), 2000);
            } else {
                setShowCorrectionCard(true);
            }
        }
    }, [feedback, showCorrectionCard, currentQuestion, streak, score, correctAnswers, lives, moveToNextWord, endGame]);

    const resetGame = () => {
        setGameState('menu');
        setError('');
        setSelectedCategory('in_on_at');
    };
    
    // --- Renderizado de Componentes ---

    const renderMenu = () => (
        <div className="wi-menu">
            <div className="wi-header">
                <Target size={48} />
                <h1>Where Is It?</h1>
                <p>Domina las preposiciones en inglés eligiendo la opción correcta.</p>
            </div>
            <div className="wi-dashboard">
                <div><Star size={16} /><span>Points</span><strong>{highScore}</strong></div>
                <div><Zap size={16} /><span>Racha</span><strong>0</strong></div>
                <div><Heart size={16} /><span>Vidas</span><strong>{INITIAL_LIVES}</strong></div>
            </div>
            <div className="wi-config-section">
                <h3>Elige una Categoría para Practicar</h3>
                <div className="wi-category-grid">
                    {Object.entries(CATEGORIES).map(([key, cat]) => (
                        <button key={key} onClick={() => setSelectedCategory(key)} className={`wi-btn ${selectedCategory === key ? 'active' : ''}`}>
                            {cat.icon} {cat.name}
                        </button>
                    ))}
                </div>
            </div>
            {error && <p className="wi-error-message">{error}</p>}
            <button className="wi-play-btn" onClick={startGame} disabled={isLoading || !selectedCategory}>
                {isLoading ? <><Loader className="animate-spin"/> Buscando...</> : '¡Comenzar Búsqueda!'}
            </button>
        </div>
    );

    const renderGame = () => (
        <div className="wi-playing-view">
            {showCorrectionCard && (
                <div className="wi-feedback-overlay">
                    <div className="wi-feedback-card incorrect">
                        <XCircle size={48} />
                        <h2>¡Incorrecto!</h2>
                        <p>{currentQuestion.explanation}</p>
                        <strong>Respuesta: {currentQuestion.correct_option}</strong>
                        <button className="wi-play-btn" onClick={moveToNextWord}>Continuar</button>
                    </div>
                </div>
            )}
            <div className="wi-game-hud">
                <div className="wi-hud-item score"><Star size={16}/> {score}</div>
                <div className="wi-hud-item lives">
                    {Array.from({ length: INITIAL_LIVES }).map((_, i) => (
                        <Heart key={i} size={20} className={i < lives ? 'filled' : 'empty'}/>
                    ))}
                </div>
                <div className="wi-hud-item streak"><Zap size={16}/> x{streak}</div>
            </div>
            <div className="wi-game-area">
                <div className="wi-question-card">
                    <div className="wi-image-container">
                        <span className="wi-image-emoji">{currentQuestion?.image_emoji}</span>
                    </div>
                    <p className="wi-sentence-prompt">
                        {currentQuestion?.sentence.split('___')[0]}
                        <span className="wi-blank">___</span>
                        {currentQuestion?.sentence.split('___')[1]}
                    </p>
                </div>
                <div className="wi-answer-grid">
                    {currentQuestion?.options.map((option, index) => {
                         let btnClass = 'wi-option-btn';
                         if (feedback || showCorrectionCard) {
                             if (option.toLowerCase() === currentQuestion.correct_option.toLowerCase()) {
                                 btnClass += ' correct';
                             } else if (option === userAnswer) {
                                 btnClass += ' incorrect';
                             }
                         }
                        return (
                            <button 
                                key={index}
                                className={btnClass}
                                onClick={() => handleAnswer(option)}
                                disabled={!!feedback || showCorrectionCard}
                            >
                                {option}
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );

    const renderGameOver = () => (
        <div className="wi-game-over-screen">
            <Trophy size={48} />
            <h2>¡Búsqueda Completada!</h2>
            <p>Tu puntaje final fue:</p>
            <strong className="wi-final-score">{score}</strong>
            <button className="wi-play-btn" onClick={resetGame}><Repeat size={16}/> Jugar de Nuevo</button>
        </div>
    );
    
    return (
        <div className="wi-container">
            <div className={`wi-inner-container ${gameState}`}>
                {gameState === 'menu' && renderMenu()}
                {gameState === 'playing' && currentQuestion && renderGame()}
                {gameState === 'gameOver' && renderGameOver()}
            </div>
        </div>
    );
}

export default WhereIsIt;